/**
 * Word-level redline between the current verbatim and a proposed wording.
 *
 * This is how a claim is read at the bargaining table: not two blocks of
 * text, but one text with the deletions struck and the insertions marked.
 * The diff must therefore be conservative and exact — a redline that shows a
 * word as unchanged when it moved, or invents a boundary inside a number,
 * misstates the proposal. "28 periods" changing to "24 periods" must render
 * as one deletion and one insertion, never as a partial-word smear.
 *
 * Tokens are words with their trailing whitespace attached, so joining any
 * rendering of the segments reproduces the exact input strings — the same
 * never-lose-a-character rule the reader's segmenter obeys.
 */
/** Splits into word tokens, each carrying its trailing whitespace. */
function tokens(text) {
    return String(text ?? '').match(/\S+\s*|\s+/g) ?? [];
}
/** A token comparison that ignores trailing whitespace, so a reflowed line
 *  is not a hundred phantom edits. */
const strip = (token) => token.replace(/\s+$/, '');
/**
 * Longest common subsequence over word tokens.
 *
 * Clause texts run to a few hundred words, so the quadratic table is at most
 * a few hundred thousand cells — instant, and exact. Approximate diffs are
 * how a redline lies.
 */
function lcsTable(a, b) {
    const width = b.length + 1;
    const table = new Uint32Array((a.length + 1) * width);
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            table[i * width + j] =
                strip(a[i]) === strip(b[j])
                    ? table[(i + 1) * width + j + 1] + 1
                    : Math.max(table[(i + 1) * width + j], table[i * width + j + 1]);
        }
    }
    return table;
}
/**
 * The redline: `current` with deletions struck, insertions from `proposed`.
 *
 * Adjacent tokens of the same kind are merged, so a run of deleted words is
 * one segment rather than one per word. Deletions are emitted before the
 * insertions that replace them, which is how legal redlines read.
 */
export function redline(current, proposed) {
    const a = tokens(current);
    const b = tokens(proposed);
    const width = b.length + 1;
    const table = lcsTable(a, b);
    const out = [];
    const push = (kind, text) => {
        if (!text)
            return;
        const last = out[out.length - 1];
        if (last && last.kind === kind)
            last.text += text;
        else
            out.push({ text, kind });
    };
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
        if (strip(a[i]) === strip(b[j])) {
            // The shared word keeps the *proposed* spacing, so the joined
            // insertion-side text reproduces the proposal exactly.
            push('same', b[j]);
            i++;
            j++;
        }
        else if (table[(i + 1) * width + j] >= table[i * width + j + 1]) {
            push('del', a[i]);
            i++;
        }
        else {
            push('ins', b[j]);
            j++;
        }
    }
    while (i < a.length)
        push('del', a[i++]);
    while (j < b.length)
        push('ins', b[j++]);
    return out;
}
/** The proposal as it would read if accepted: everything except deletions. */
export function proposedTextOf(segments) {
    return segments
        .filter((s) => s.kind !== 'del')
        .map((s) => s.text)
        .join('');
}
/** The current text: everything except insertions. */
export function currentTextOf(segments) {
    return segments
        .filter((s) => s.kind !== 'ins')
        .map((s) => s.text)
        .join('');
}
//# sourceMappingURL=redline.js.map